/* PDP upsell rows — checkbox build (owner decision 2026-08-21), and the
 * "Show N more" toggle.
 *
 * Ticked rows ride the main add-to-cart button: one /cart/add call adds the
 * product on screen plus every ticked row. Two coordinated interceptors exist
 * on a PDP:
 *
 *   - a set picker in the scope OWNS the add. Its multiAdd pulls ticked upsell
 *     rows through window.AfroylaUpsellRiders (exposed here) so members and
 *     upsell rows land in the same call. This module then never intercepts.
 *   - no set picker in the scope: this module intercepts the ATC click in
 *     capture phase (same pattern, same reason: the theme adds from the click,
 *     not the submit) whenever at least one row is ticked.
 *
 * The button's price pill stays honest the same way the set picker keeps it
 * honest: ticked rows write data-set-base-cents / data-set-payable-cents onto
 * the button (the pill's set-override contract) — but only in the setless
 * case, because the set picker is the single writer whenever it exists.
 *
 * The pre-order properties are not written here. afroyla-preorder-cart.js
 * patches fetch itself, and every row renders its own registry blob, so a
 * pre-order line added this way carries the same disclosure as a line added
 * from the product form.
 *
 * Money mirrors the coupon strip and the set picker: savings floor per item,
 * and a row only drops to its payable when the code the shopper claimed on
 * this page is the code that covers that row. Liquid resolves each row's own
 * code and saving; this file only decides whether to show it. */
(function () {
  'use strict';

  // Theme-editor section re-renders re-execute this script; without a guard
  // the document-level click/coupon listeners stack on every re-render.
  if (window.__aflPdpUpsellInit) return;
  window.__aflPdpUpsellInit = true;

  function scopeOf(node) {
    return node.closest('.shopify-section') || node.closest('[data-section-id]') || document;
  }

  function stripOf(scope) {
    return scope.querySelector('[data-coupon-strip]');
  }

  function claimedCode(scope) {
    var s = stripOf(scope);
    if (!s) return null;
    var code = s.getAttribute('data-coupon-code');
    if (!code) return null;
    if (s.getAttribute('data-state') === 'claimed') return code;
    try {
      return window.localStorage.getItem('afroyla:coupon:' + code) === '1' ? code : null;
    } catch (e) {
      return null;
    }
  }

  /* Same per-item floor as the strip, the pill, and the set picker. */
  function stripSavings(cents, scope) {
    var s = stripOf(scope);
    if (!s) return 0;
    var fixed = parseInt(s.getAttribute('data-fixed-cents') || '0', 10);
    var pct = parseFloat(s.getAttribute('data-discount-pct') || '0');
    var save = 0;
    if (fixed > 0) save = fixed;
    else if (pct > 0) save = Math.floor((cents * pct) / 100);
    return Math.min(save, cents);
  }

  function money(cents) {
    if (window.theme && typeof window.theme.formatMoney === 'function') {
      return window.theme.formatMoney(cents, window.theme.moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /* One price fragment: "+ payable" + struck reference. Covered-and-claimed
   * strikes the base; otherwise the compare-at, and only when it is a real
   * markup. */
  function paintPrice(el, code) {
    var cents = parseInt(el.getAttribute('data-up-cents') || '0', 10);
    var compare = parseInt(el.getAttribute('data-up-compare') || '0', 10);
    var save = parseInt(el.getAttribute('data-up-save') || '0', 10);
    var covered = !!code && save > 0 && el.getAttribute('data-up-code') === code;

    var show = covered ? cents - save : cents;
    var strike = covered ? cents : (compare > cents ? compare : 0);

    el.textContent = '';
    el.appendChild(document.createTextNode('+ ' + money(show)));
    if (strike && strike !== show) {
      var s = document.createElement('s');
      s.className = 'afroyla-up__was';
      s.textContent = money(strike);
      el.appendChild(s);
    }
  }

  function paintAll(root) {
    (root || document).querySelectorAll('[data-up-section]').forEach(function (section) {
      var code = claimedCode(scopeOf(section));
      section.querySelectorAll('[data-up-price]').forEach(function (el) {
        paintPrice(el, code);
      });
    });
  }

  /* ---- riders: the ticked rows, as the batch-add sees them ---------------- */

  function riders(scope) {
    var out = [];
    (scope || document).querySelectorAll('[data-up-check]').forEach(function (input) {
      if (!input.checked) return;
      out.push({
        vid: parseInt(input.getAttribute('data-up-vid'), 10),
        cents: parseInt(input.getAttribute('data-up-cents') || '0', 10),
        save: parseInt(input.getAttribute('data-up-save') || '0', 10),
        code: input.getAttribute('data-up-code') || '',
        source: input.getAttribute('data-up-source') || 'pdp-upsell'
      });
    });
    return out;
  }

  function uncheckAll(scope) {
    (scope || document).querySelectorAll('[data-up-check]').forEach(function (input) {
      input.checked = false;
      var row = input.closest('.afroyla-up__row');
      if (row) row.classList.remove('is-checked');
    });
  }

  // The set picker's multiAdd and totals pull ticked rows through this pair.
  window.AfroylaUpsellRiders = riders;
  window.AfroylaUpsellUncheck = uncheckAll;

  function hasSetPicker(scope) {
    return !!scope.querySelector('[data-set-picker]');
  }

  /* Pill contract: with rows ticked and no set picker, the button shows the
   * batch total exactly the way it shows a set total. The set picker is the
   * single writer of these attributes whenever it exists in the scope. */
  function broadcast(scope) {
    if (hasSetPicker(scope)) return;
    var list = riders(scope);
    var code = claimedCode(scope);
    scope.querySelectorAll('[data-add-to-cart]').forEach(function (btn) {
      if (!list.length) {
        btn.removeAttribute('data-set-base-cents');
        btn.removeAttribute('data-set-payable-cents');
        return;
      }
      var anchorCents = parseInt(btn.getAttribute('data-price-cents') || '0', 10);
      var base = anchorCents;
      var payable = anchorCents - stripSavings(anchorCents, scope);
      list.forEach(function (r) {
        base += r.cents;
        payable += r.cents - (code && r.code === code ? r.save : 0);
      });
      btn.setAttribute('data-set-base-cents', base);
      btn.setAttribute('data-set-payable-cents', payable);
    });
    document.dispatchEvent(new CustomEvent('afroyla:set:price', { bubbles: true }));
  }

  function broadcastAll() {
    document.querySelectorAll('[data-up-section]').forEach(function (section) {
      broadcast(scopeOf(section));
    });
  }

  /* ---- setless multi-add --------------------------------------------------- */

  function cartAddUrl() {
    return (window.theme && window.theme.routes && window.theme.routes.cartAdd) || '/cart/add.js';
  }

  function multiAdd(event, form, scope) {
    var list = riders(scope);
    if (!list.length) return;
    event.preventDefault();
    event.stopImmediatePropagation();

    var idInput = form.querySelector('input[name="id"]');
    var vid = idInput ? parseInt(idInput.value, 10) : 0;
    var qtyInput = form.querySelector('input[name="quantity"]');
    var qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;

    var items = [{ id: vid, quantity: qty }];
    list.forEach(function (r) {
      items.push({ id: r.vid, quantity: 1, properties: { _attach_source: r.source } });
    });

    var btn = form.querySelector('[data-add-to-cart]');
    if (btn) btn.classList.add('is-loading');

    fetch(cartAddUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('add failed: ' + res.status);
        return res.json();
      })
      .then(function () {
        uncheckAll(scope);
        broadcast(scope);
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('theme:product:added', { bubbles: true }));
      })
      .catch(function () {
        // Never leave her stuck: hand the click back to the theme's own
        // single-add path (mirrors the set picker's fallback).
        uncheckAll(scope);
        broadcast(scope);
        if (form.requestSubmit) form.requestSubmit();
      })
      .then(function () {
        if (btn) btn.classList.remove('is-loading');
      });
  }

  /* ---- Size & photos quick-view -------------------------------------------- */

  /* Per-row <template data-up-qv-tpl> cloned into the section's [data-up-qv]
   * panel — the upsell twin of the set picker's sheet. The ADD button ticks
   * the row's checkbox (checkbox build: the actual add still rides the main
   * ATC), so it re-uses the existing change pipeline for row state, pill
   * broadcast and rider totals. */
  var qvSection = null;
  var qvIdx = null;

  function qvPanel(section) {
    return section.querySelector('[data-up-qv]');
  }

  function qvRowInput(section, idx) {
    var row = section.querySelector('[data-up-row="' + idx + '"]');
    return row ? row.querySelector('[data-up-check]') : null;
  }

  function paintQvAdd() {
    if (qvSection === null || qvIdx === null) return;
    var qv = qvPanel(qvSection);
    var add = qv && qv.querySelector('[data-up-qv-add]');
    var input = qvRowInput(qvSection, qvIdx);
    if (!add || !input) return;
    if (input.checked) {
      add.textContent = 'Added ✓';
      add.classList.add('is-added');
      return;
    }
    var cents = parseInt(input.getAttribute('data-up-cents') || '0', 10);
    var save = parseInt(input.getAttribute('data-up-save') || '0', 10);
    var code = claimedCode(scopeOf(qvSection));
    var covered = !!code && save > 0 && input.getAttribute('data-up-code') === code;
    add.textContent = 'Add — ' + money(covered ? cents - save : cents);
    add.classList.remove('is-added');
  }

  function openQv(section, idx) {
    var qv = qvPanel(section);
    var slot = qv && qv.querySelector('[data-up-qv-slot]');
    var tpl = section.querySelector('[data-up-qv-tpl="' + idx + '"]');
    if (!slot || !tpl) return;
    qvSection = section;
    qvIdx = idx;
    slot.innerHTML = '';
    slot.appendChild(tpl.content.cloneNode(true));
    var code = claimedCode(scopeOf(section));
    slot.querySelectorAll('[data-up-price]').forEach(function (el) {
      paintPrice(el, code);
    });
    paintQvAdd();
    qv.removeAttribute('hidden');
    document.documentElement.classList.add('afroyla-up-qv-open');
  }

  function closeQv() {
    if (qvSection === null) return;
    var qv = qvPanel(qvSection);
    if (qv) qv.setAttribute('hidden', '');
    qvSection = null;
    qvIdx = null;
    document.documentElement.classList.remove('afroyla-up-qv-open');
  }

  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;
    var opener = event.target.closest('[data-up-qv-open]');
    if (opener) {
      // The opener sits inside the row <label>; stop the label from also
      // toggling the checkbox underneath the tap.
      event.preventDefault();
      var section = opener.closest('[data-up-section]');
      if (section) openQv(section, opener.getAttribute('data-up-qv-open'));
      return;
    }
    if (event.target.closest('[data-up-qv-close]')) {
      closeQv();
      return;
    }
    if (event.target.closest('[data-up-qv-add]') && qvSection !== null) {
      var input = qvRowInput(qvSection, qvIdx);
      if (!input) return;
      input.checked = !input.checked;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      paintQvAdd();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeQv();
  });

  /* ---- events -------------------------------------------------------------- */

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input.matches || !input.matches('[data-up-check]')) return;
    var row = input.closest('.afroyla-up__row');
    if (row) row.classList.toggle('is-checked', input.checked);
    var scope = scopeOf(input);
    broadcast(scope);
    // The set picker (when present) folds riders into its own totals/pill.
    document.dispatchEvent(new CustomEvent('afroyla:riders:change', { bubbles: true }));
  });

  document.addEventListener('click', function (event) {
    var more = event.target.closest && event.target.closest('[data-up-more]');
    if (!more) return;
    event.preventDefault();
    var section = more.closest('[data-up-section]');
    if (!section) return;
    section.querySelectorAll('[data-up-extra]').forEach(function (row) {
      row.hidden = false;
    });
    more.remove();
  });

  // Setless ATC intercept — capture phase, before the theme's own listener.
  document.addEventListener('click', function (event) {
    var btn = event.target.closest && event.target.closest('[data-add-to-cart]');
    if (!btn) return;
    var form = btn.closest('form[data-product-form]');
    if (!form) return;
    var scope = scopeOf(form);
    if (hasSetPicker(scope)) return; // the set picker owns the add
    if (!scope.querySelector('[data-up-section]')) return;
    multiAdd(event, form, scope);
  }, true);

  /* Unclaiming repaints too. Watching only the claim left these rows showing
   * a payable the shopper had already given back, contradicting the headline
   * beside them. The broadcast keeps the button pill on the same truth. */
  ['afroyla:coupon:claimed', 'afroyla:discount:removed', 'afroyla:discount:applied'].forEach(
    function (name) {
      document.addEventListener(name, function () {
        paintAll(document);
        broadcastAll();
        paintQvAdd();
      });
    }
  );

  // Variant switch moves data-price-cents on the button (the pill's own
  // listener writes it); re-broadcast so the batch total follows the anchor.
  document.addEventListener('theme:variant:change', function () {
    broadcastAll();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { paintAll(document); });
  } else {
    paintAll(document);
  }

  /* Sibling-size navigation swaps the whole product section in place, so fresh
   * rows can arrive long after load. */
  if (window.MutationObserver) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.querySelector && node.querySelector('[data-up-section]')) paintAll(node);
        }
      }
      // A section swap can replace the DOM under an open sheet — drop the
      // stale reference and release the scroll lock.
      if (qvSection !== null && !document.contains(qvSection)) {
        qvSection = null;
        qvIdx = null;
        document.documentElement.classList.remove('afroyla-up-qv-open');
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
